/* usearch.js — universal search bar behaviour (v5 spec: design/navbar-mockup.html).
 * The header's magnifier morphs into the single ×; the bar is a flex sibling
 * at navbar height. Works on every page: grid pages get the live Typesense
 * island bound into the slot (typesense-plp.js); elsewhere Enter submits to
 * /search. Behaviours: toggle morph · type-anywhere · "/" opens · esc closes
 * (unadvertised) · click × clears query / closes · HOLD × (600ms, progress
 * ring) purges query + all filters (usearch:purge event; island listens).
 */
(() => {
  'use strict';
  const header = document.querySelector('.site-header');
  if (!header) return;
  const bar = header.querySelector('[data-usearch]');
  const toggle = header.querySelector('[data-usearch-toggle]');
  if (!bar || !toggle) return;

  // STATIC mode (tabbed header, sections/header.liquid): the bar is always
  // visible, so "open" means focus and "close" means blur — nothing is ever
  // hidden, the header never gains is-searching (which hides the logo at
  // <=900px), and the tabs beneath can never be covered. Everything else —
  // type-anywhere, "/", hold-to-purge, the takeover — is shared.
  const isStatic = bar.hasAttribute('data-usearch-static');
  // DIALOG mode (sections/header.liquid search_dialog): pages without a grid
  // get the focus popover / typed dialog (search-dialog.js) instead of the
  // in-place takeover below, so the island is not prefetched and the first
  // keystroke never swaps the page. Grid pages are unaffected either way.
  const isDialog = bar.hasAttribute('data-usearch-dialog');

  const HOLD_MS = 600;
  let holdTimer = null;
  let heldFired = false;

  const activeInput = () => {
    const slotInput = bar.querySelector('[data-usearch-slot]:not([hidden]) input');
    return slotInput || bar.querySelector('[data-usearch-input]');
  };
  const isOpen = () => isStatic
    ? bar.contains(document.activeElement)
    : header.classList.contains('is-searching');
  // the "/" hint lives inside the input (header-tabbed.css .usearch__kbd):
  // CSS hides it on focus; this class hides it while a query is present. The
  // island swaps its own input into the slot after boot with ?q= already in
  // it, so re-check on load and once more after the island has had its turn.
  const syncQuery = () => bar.classList.toggle('has-query', !!queryText());
  bar.addEventListener('input', syncQuery);
  window.addEventListener('load', syncQuery);
  setTimeout(syncQuery, 1500);
  syncQuery();

  function open() {
    if (!isStatic) {
      bar.hidden = false;
      header.classList.add('is-searching');
    }
    toggle.setAttribute('aria-expanded', 'true');
    if (!isDialog) loadIsland(); // prefetch the grid island so the first keystroke is instant
    const input = activeInput();
    if (input) setTimeout(() => input.focus(), isStatic ? 0 : 100);
  }
  function close() {
    toggle.setAttribute('aria-expanded', 'false');
    if (isStatic) {
      const input = activeInput();
      if (input && document.activeElement === input) input.blur();
      return;
    }
    header.classList.remove('is-searching');
    bar.hidden = true;
  }
  function queryText() {
    const input = activeInput();
    return input ? input.value.trim() : '';
  }
  function clearQuery() {
    const input = activeInput();
    if (!input) return;
    // native setter so frameworks (InstantSearch) hear the change
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    setter.call(input, '');
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  }

  // click: query present → clear it; empty → close. hold: purge everything.
  toggle.addEventListener('click', (e) => {
    e.preventDefault();
    if (heldFired) { heldFired = false; return; }
    if (!isOpen()) { open(); return; }
    if (queryText()) clearQuery();
    else close();
  });
  const startHold = () => {
    if (!isOpen()) return;
    toggle.classList.add('is-holding');
    holdTimer = setTimeout(() => {
      heldFired = true;
      toggle.classList.remove('is-holding');
      clearQuery();
      document.dispatchEvent(new CustomEvent('usearch:purge'));
    }, HOLD_MS);
  };
  const cancelHold = () => {
    toggle.classList.remove('is-holding');
    if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
  };
  toggle.addEventListener('pointerdown', startHold);
  toggle.addEventListener('pointerup', cancelHold);
  toggle.addEventListener('pointerleave', cancelHold);

  // --- search takeover: on pages without a grid, the FIRST typed character
  // swaps the page body for the live search grid in place — no reload — and
  // pushes /search so the URL matches what's on screen. Grid pages (which
  // already carry a [data-ts-plp] root) are untouched.
  const PLP_SRC = bar.getAttribute('data-plp-src');
  let islandLoaded = false;
  let tookOver = false;
  function loadIsland() {
    if (islandLoaded || !PLP_SRC) return;
    islandLoaded = true;
    // warm the vendored InstantSearch libs alongside the island script so the
    // first keystroke's takeover renders without a library round-trip
    const libs = (window.TYPESENSE || {}).libs || {};
    ['is', 'adapter'].forEach((k) => {
      if (!libs[k]) return;
      const l = document.createElement('link');
      l.rel = 'preload';
      l.as = 'script';
      l.href = libs[k];
      document.head.appendChild(l);
    });
    if (document.querySelector('script[src*="typesense-plp"]')) return;
    const s = document.createElement('script');
    s.src = PLP_SRC;
    s.defer = true;
    document.head.appendChild(s);
  }
  function takeover() {
    if (tookOver || document.querySelector('[data-ts-plp]')) return;
    const main = document.querySelector('main');
    if (!main) return;
    tookOver = true;
    Array.from(main.children).forEach((el) => { el.hidden = true; });
    const sec = document.createElement('section');
    sec.className = 'collection collection--wide';
    let skel = '<aside class="plp__rail plp__rail--skeleton" aria-hidden="true"><div class="skel skel--btn"></div>';
    for (let i = 0; i < 4; i++) skel += '<div class="skel-group"><div class="skel skel--label"></div><div class="skel"></div><div class="skel"></div><div class="skel skel--short"></div></div>';
    skel += '</aside>';
    sec.innerHTML = '<header class="collection__head"><p class="eyebrow">Search</p></header>' +
      '<div class="plp" data-ts-plp>' + skel + '<div class="plp__main"></div></div>';
    main.appendChild(sec);
    history.pushState({ usearchTakeover: true }, '', '/search' + location.search);
    document.dispatchEvent(new CustomEvent('usearch:takeover'));
  }
  bar.addEventListener('input', () => { if (!isDialog && queryText()) takeover(); });
  // leaving the takeover via back/forward: reload so the original page comes
  // back whole (the island owns too much live state to unwind in place)
  window.addEventListener('popstate', () => { if (tookOver) location.reload(); });

  // keyboard: "/" opens from anywhere; esc closes; while open, printable keys
  // land in the bar no matter where the cursor is
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) {
      // the dialog takes the first Escape (closes, keeps the caret); ours blurs
      if (isDialog && bar.classList.contains('is-dialog-open')) return;
      close(); return;
    }
    const inField = e.target.closest && e.target.closest('input, textarea, select, [contenteditable]');
    if (e.key === '/' && !inField) { e.preventDefault(); open(); return; }
    if (isOpen() && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && !inField) {
      const input = activeInput();
      if (input) input.focus();
    }
  });
})();
